import { useState, useContext, KeyboardEvent, useEffect } from 'react';

import { useNavigate } from 'react-router-dom';

import { useQuery } from '@tanstack/react-query';

import {
  Flex,
  Text,
  useColorModeValue,
  Alert,
  AlertIcon,
  AlertTitle,
  AlertDescription,
  Table,
  Thead,
  Tr,
  Th,
  Tbody,
  Td,
  Box,
  Button,
  Spinner,
  InputGroup,
  Input,
  Stack,
  Select,
  Collapse,
  IconButton,
  Tooltip,
} from '@chakra-ui/react';

import {
  AddIcon,
  SearchIcon,
  SmallCloseIcon,
  RepeatIcon,
} from '@chakra-ui/icons';

import SelectSearch from 'components/fields/SelectSearch';
import Paginate from 'components/paginate/Paginate';
import Card from 'components/card/Card';

import getDateString from 'helpers/getDateString';

import { apiGetUsers, apiGetBranches } from 'api';

import type {
  APIPaginatedResponse,
  APIUserListResultData,
  APIBranchData,
} from 'types/api.types';

import { AuthContext } from 'contexts/AuthContext';

import { useUrlFilters } from 'hooks/useUrlFilters';

import { ROLE_ADMIN } from 'variables/constants';
import { ROLES_OPTIONS } from 'variables/options';

const TABLE_HEADERS = [
  'First Name',
  'Last Name',
  'Email',
  'Phone',
  'Date joined',
  'Last login',
  'Branch',
  'Role',
];

const USER_SEARCH_KEYS = ['name', 'email', 'role', 'branch', 'is_active'] as const;

const UserListView = () => {
  const { user } = useContext(AuthContext);
  const navigate = useNavigate();
  const {
    setFilters,
    page,
    setPage,
    resetAll,
    queryString,
    searchParams,
  } = useUrlFilters(USER_SEARCH_KEYS);

  const textColor = useColorModeValue('secondaryGray.900', 'white');
  const borderColor = useColorModeValue('gray.200', 'whiteAlpha.100');
  const inputBg = useColorModeValue('white', 'navy.900');
  const hoverBgColor = useColorModeValue('gray.200', 'whiteAlpha.100');

  const [showSearchBar, setShowSearchBar] = useState(false);
  const [nameSearch, setNameSearch] = useState('');
  const [emailSearch, setEmailSearch] = useState('');
  const [selectedRole, setSelectedRole] = useState('');
  const [selectedBranch, setSelectedBranch] = useState('');
  const [selectedIsActive, setSelectedIsActive] = useState('');

  useEffect(() => {
    setNameSearch(searchParams.get('name') ?? '');
    setEmailSearch(searchParams.get('email') ?? '');
    setSelectedRole(searchParams.get('role') ?? '');
    setSelectedBranch(searchParams.get('branch') ?? '');
    setSelectedIsActive(searchParams.get('is_active') ?? '');
  }, [searchParams]);

  const { error, data, isLoading, isFetching, refetch } = useQuery({
    queryKey: ['user-list', page, queryString],
    queryFn: () => apiGetUsers(page + 1, queryString),
  }) as {
    data: APIPaginatedResponse<APIUserListResultData>;
    isLoading: boolean;
    error: any;
    isFetching: boolean;
    refetch: () => void;
  };

  const { data: branchesData, isLoading: isLoadingBranches } = useQuery({
    queryKey: ['branches-all'],
    queryFn: () => apiGetBranches(null, 'all'),
    staleTime: 5 * 60 * 1000,
  }) as {
    data: APIBranchData[];
    isLoading: boolean;
    error: any;
  };

  const handleEditUser = (userData: APIUserListResultData) => {
    navigate(`/admin/settings/users/edit/${userData.user.id}`);
  };

  const handleCreateUser = () => {
    navigate('/admin/settings/users/new');
  };

  const handleSearch = () => {
    setFilters({
      name: nameSearch.trim(),
      email: emailSearch.trim(),
      role: selectedRole.trim(),
      branch: selectedBranch.trim(),
      is_active: selectedIsActive.trim(),
    });
  };

  const resetSearch = () => {
    setNameSearch('');
    setEmailSearch('');
    setSelectedRole('');
    setSelectedBranch('');
    setSelectedIsActive('');
    resetAll();
  };

  const handleKeyDown = (
    e: KeyboardEvent<HTMLInputElement | HTMLSelectElement>,
  ) => {
    if (e.key === 'Enter') {
      handleSearch();
    }
  };

  const canEditMember = (member: APIUserListResultData) => {
    if (user.role === ROLE_ADMIN) return true;
    return member.user.email !== user.email;
  };

  if (error) {
    return (
      <Card>
        <Box p="20px" pb="0">
          <Alert status="error">
            <AlertIcon />
            <AlertTitle>Error</AlertTitle>
            <AlertDescription>{JSON.stringify(error)}</AlertDescription>
          </Alert>
        </Box>
      </Card>
    );
  }

  return (
    <Card
      flexDirection="column"
      w="100%"
      px="0px"
      overflowX={{ sm: 'scroll', lg: 'hidden' }}
    >
      <Flex px="20px" justifyContent="space-between" align="center" mb="20px">
        <Text
          color={textColor}
          fontSize="22px"
          fontWeight="700"
          lineHeight="100%"
        >
          Users
        </Text>
        <Flex gap={2}>
          <Tooltip
            label={showSearchBar ? 'Hide search' : 'Show search'}
            hasArrow
          >
            <IconButton
              aria-label={showSearchBar ? 'Hide search' : 'Show search'}
              icon={<SearchIcon />}
              variant={showSearchBar ? 'solid' : 'ghost'}
              colorScheme={showSearchBar ? 'yellow' : 'gray'}
              onClick={() => setShowSearchBar(!showSearchBar)}
              size="md"
            />
          </Tooltip>
          <Tooltip label="Refresh users" hasArrow>
            <IconButton
              aria-label="Refresh users"
              icon={<RepeatIcon />}
              onClick={refetch}
              variant="ghost"
              isLoading={isLoading || isFetching}
            />
          </Tooltip>
          {user.role === ROLE_ADMIN && (
            <Button
              leftIcon={<AddIcon />}
              variant="brand"
              size="md"
              onClick={handleCreateUser}
            >
              New User
            </Button>
          )}
        </Flex>
      </Flex>

      <Collapse in={showSearchBar} animateOpacity>
        <Box
          px="20px"
          pb="20px"
          borderBottom="1px solid"
          borderColor={borderColor}
        >
          <Stack
            spacing={4}
            direction={{ base: 'column', lg: 'row' }}
            align="flex-end"
          >
            <Box flex="1">
              <InputGroup>
                <Input
                  variant="main"
                  placeholder="Search by name"
                  value={nameSearch}
                  onChange={(e) => setNameSearch(e.target.value)}
                  bg={inputBg}
                  size="md"
                  onKeyDown={handleKeyDown}
                />
              </InputGroup>
            </Box>

            <Box flex="1">
              <InputGroup>
                <Input
                  variant="main"
                  placeholder="Search by email"
                  value={emailSearch}
                  onChange={(e) => setEmailSearch(e.target.value)}
                  bg={inputBg}
                  size="md"
                  onKeyDown={handleKeyDown}
                />
              </InputGroup>
            </Box>

            <Box flex="1">
              <Select
                variant="main"
                placeholder="All roles"
                value={selectedRole}
                onChange={(e) => setSelectedRole(e.target.value)}
                bg={inputBg}
                size="md"
                onKeyDown={handleKeyDown}
              >
                {ROLES_OPTIONS.map((role) => (
                  <option key={role.value} value={role.value}>
                    {role.label}
                  </option>
                ))}
              </Select>
            </Box>

            <Box flex="1">
              <SelectSearch
                options={
                  branchesData?.map((branch) => ({
                    value: branch.id.toString(),
                    label: branch.name,
                  })) || []
                }
                value={selectedBranch}
                onChange={(value: any) =>
                  setSelectedBranch(value?.toString() || '')
                }
                placeholder="All branches"
                isDisabled={isLoadingBranches}
                allowClear
              />
            </Box>

            <Box flex="1">
              <Select
                variant="main"
                placeholder="Status"
                value={selectedIsActive}
                onChange={(e) => setSelectedIsActive(e.target.value)}
                bg={inputBg}
                size="md"
                onKeyDown={handleKeyDown}
              >
                <option value="true">Active</option>
                <option value="false">Inactive</option>
              </Select>
            </Box>

            <Flex gap={2}>
              <Button
                onClick={handleSearch}
                leftIcon={<SearchIcon />}
                size="md"
                variant="brand"
              >
                Search
              </Button>
              <Button
                onClick={resetSearch}
                variant="outline"
                leftIcon={<SmallCloseIcon />}
                size="md"
              >
                Reset
              </Button>
            </Flex>
          </Stack>
        </Box>
      </Collapse>

      <Table variant="simple" color="gray.500">
        <Thead>
          <Tr>
            {TABLE_HEADERS.map((header) => (
              <Th
                key={header}
                pe="10px"
                borderColor={borderColor}
                color="gray.400"
              >
                {header}
              </Th>
            ))}
          </Tr>
        </Thead>
        <Tbody>
          {isLoading && (
            <Tr>
              <Td colSpan={7} textAlign="center">
                <Flex align="center" justify="center" mt="20px">
                  <Spinner size="sm" mr="10px" /> Loading users...
                </Flex>
              </Td>
            </Tr>
          )}

          {data?.results.map((member) => (
            <Tr
              key={member.user.id}
              cursor={canEditMember(member) ? 'pointer' : 'default'}
              _hover={{
                bg: canEditMember(member) ? hoverBgColor : 'inherit',
              }}
              onClick={() => {
                if (canEditMember(member)) {
                  handleEditUser(member);
                }
              }}
            >
              <Td>
                <Flex align="center">
                  <Text color={textColor} fontSize="sm" fontWeight="700">
                    {member.user.first_name}
                  </Text>
                </Flex>
              </Td>
              <Td>
                <Flex align="center">
                  <Text color={textColor} fontSize="sm" fontWeight="700">
                    {member.user.last_name}
                  </Text>
                </Flex>
              </Td>
              <Td>
                <Flex align="center">
                  <Text color={textColor} fontSize="sm" fontWeight="700">
                    {member.user.email}
                  </Text>
                </Flex>
              </Td>
              <Td>
                <Flex align="center">
                  <Text color={textColor} fontSize="sm" fontWeight="700">
                    {member.phone || 'N/A'}
                  </Text>
                </Flex>
              </Td>
              <Td>
                <Flex align="center">
                  <Text color={textColor} fontSize="sm">
                    {getDateString(new Date(member.user.date_joined))}
                  </Text>
                </Flex>
              </Td>
              <Td>
                <Flex align="center">
                  <Text color={textColor} fontSize="sm">
                    {member.user.last_login
                      ? getDateString(new Date(member.user.last_login))
                      : 'Never'}
                  </Text>
                </Flex>
              </Td>
              <Td>
                <Flex align="center">
                  <Text color={textColor} fontSize="sm">
                    {member.branches.map((branch) => branch.name).join(', ') ||
                      'N/A'}
                  </Text>
                </Flex>
              </Td>
              <Td>
                <Flex align="center">
                  <Text color={textColor} fontSize="sm" fontWeight="700">
                    {member.role_display}
                  </Text>
                </Flex>
              </Td>
            </Tr>
          ))}
        </Tbody>
      </Table>

      {data && data.results.length === 0 && (
        <Box textAlign="center" w="100%" mt="20px">
          No users found
        </Box>
      )}

      <Paginate page={page} count={data?.count || 0} onSetPage={setPage} />
    </Card>
  );
};

export default UserListView;
